import React, { useState, useEffect } from 'react';
import {
  GitCompare, Plus, Minus, Edit3, ChevronDown, ChevronRight,
  FileText, Layers, Target, AlertTriangle, Info, X, Loader2,
  CheckCircle, Clock, ArrowRight, Filter
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { WidgetErrorBoundary } from './ErrorBoundary';
import { SkeletonPaneContent } from './Skeleton';
import EmptyState from './EmptyState';

// ==========================================
// CONSTANTS
// ==========================================

const CHANGE_COLORS = {
  added: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: Plus,
    badge: 'bg-green-100 text-green-700',
  },
  removed: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: Minus,
    badge: 'bg-red-100 text-red-700',
  },
  modified: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: Edit3,
    badge: 'bg-amber-100 text-amber-700',
  },
  unchanged: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-600',
    icon: CheckCircle,
    badge: 'bg-gray-100 text-gray-600',
  },
};

const TYPE_ICONS = {
  theme: Layers,
  epic: Target,
  requirement: FileText,
  phase: Layers,
  work_package: FileText,
  task: CheckCircle,
};

// ==========================================
// DIFF SUMMARY CARD
// ==========================================

const DiffSummaryCard = ({ label, counts }) => {
  const total = (counts?.added || 0) + (counts?.removed || 0) + (counts?.modified || 0);
  
  return (
    <div className="bg-white rounded-lg border p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">{label}</h4>
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">{counts?.added || 0}</div>
          <div className="text-xs text-gray-500">Added</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-red-600">{counts?.removed || 0}</div>
          <div className="text-xs text-gray-500">Removed</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-600">{counts?.modified || 0}</div>
          <div className="text-xs text-gray-500">Modified</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-gray-600">{counts?.unchanged || 0}</div>
          <div className="text-xs text-gray-500">Unchanged</div>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500 text-center">
          {total > 0 ? `${total} changes detected` : 'No changes'}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// DIFF ITEM ROW
// ==========================================

const DiffItemRow = ({ item, changeType, showDetails = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = CHANGE_COLORS[changeType] || CHANGE_COLORS.unchanged;
  const Icon = colors.icon;
  const TypeIcon = TYPE_ICONS[item.type?.toLowerCase()] || FileText;

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} mb-2`}>
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer"
        onClick={() => item.changes && setIsExpanded(!isExpanded)}
      >
        {/* Change indicator */}
        <span className={`flex items-center justify-center w-6 h-6 rounded-full ${colors.badge}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>

        {/* Type badge */}
        <span className="text-xs px-2 py-0.5 bg-white/50 rounded border border-gray-200">
          <TypeIcon className="w-3 h-3 inline mr-1" />
          {item.type}
        </span>

        {/* Name */}
        <span className={`flex-1 text-sm font-medium ${colors.text}`}>
          {item.name}
        </span>

        {/* Category */}
        {item.category && (
          <span className="text-xs px-2 py-0.5 bg-white/50 rounded">
            {item.category}
          </span>
        )}

        {/* Expand for modified items */}
        {item.changes && item.changes.length > 0 && (
          <button className="p-1 hover:bg-white/50 rounded">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        )}
      </div>

      {/* Expanded changes view */}
      {isExpanded && item.changes && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-200/50">
          <div className="text-xs font-medium text-gray-500 mb-2">Changes:</div>
          <div className="space-y-1">
            {item.changes.map((change, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <span className="font-medium text-gray-600 w-24 shrink-0">
                  {change.field}:
                </span>
                <div className="flex-1 space-y-0.5">
                  {change.from && (
                    <div className="flex items-center gap-1">
                      <span className="text-red-500 line-through">{change.from}</span>
                    </div>
                  )}
                  {change.to && (
                    <div className="flex items-center gap-1">
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className="text-green-600">{change.to}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// DIFF SECTION
// ==========================================

const DiffSection = ({ title, items, changeType }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const colors = CHANGE_COLORS[changeType];

  if (!items || items.length === 0) return null;

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${colors.badge} mb-2`}
      >
        <span className="font-medium text-sm flex items-center gap-2">
          <colors.icon className="w-4 h-4" />
          {title}
        </span>
        <span className="flex items-center gap-2">
          <span className="text-sm">{items.length}</span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>
      </button>

      {isExpanded && (
        <div className="space-y-1">
          {items.map((item, idx) => (
            <DiffItemRow key={item.id || idx} item={item} changeType={changeType} />
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN DIFF VIEWER COMPONENT
// ==========================================

const BaselineDiffViewer = ({
  fromBaselineId,
  toBaselineId,
  tenantId,
  onClose,
  onCreateChangeRequest
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [filter, setFilter] = useState('all'); // all, requirements, wbs

  useEffect(() => {
    if (fromBaselineId && toBaselineId && tenantId) {
      loadDiff();
    }
  }, [fromBaselineId, toBaselineId, tenantId]);

  const loadDiff = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.compareBaselines(fromBaselineId, toBaselineId, tenantId);
      setDiffData(res.data);
    } catch (err) {
      console.error('Failed to load diff:', err);
      setError('Failed to compare baselines');
      toast.error('Failed to load comparison');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <SkeletonPaneContent />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-gray-600">{error}</p>
        <button
          onClick={loadDiff}
          className="mt-4 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!diffData) {
    return (
      <EmptyState
        icon={GitCompare}
        title="No comparison data"
        description="Select two baselines to compare"
      />
    );
  }

  const { from_baseline, to_baseline, summary, requirements, wbs_nodes } = diffData;

  const hasChanges = 
    (summary?.requirements?.added || 0) + 
    (summary?.requirements?.removed || 0) + 
    (summary?.requirements?.modified || 0) +
    (summary?.wbs_nodes?.added || 0) + 
    (summary?.wbs_nodes?.removed || 0) + 
    (summary?.wbs_nodes?.modified || 0) > 0;

  return (
    <WidgetErrorBoundary widgetName="Baseline Diff Viewer">
      <div className="bg-white rounded-xl shadow-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="baseline-diff-viewer">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <GitCompare className="w-6 h-6 text-[var(--clr-brand-primary)]" />
              <h2 className="text-lg font-semibold text-gray-900">Baseline Comparison</h2>
            </div>
            {onClose && (
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>

          {/* Baseline info */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200">
              <span className="text-red-600 font-medium">From:</span>
              <span>V{from_baseline?.version} ({from_baseline?.status})</span>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
              <span className="text-green-600 font-medium">To:</span>
              <span>V{to_baseline?.version} ({to_baseline?.status})</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-4">
            <DiffSummaryCard label="Requirements" counts={summary?.requirements} />
            <DiffSummaryCard label="WBS Nodes" counts={summary?.wbs_nodes} />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="px-6 py-3 border-b flex items-center gap-4">
          <span className="text-sm font-medium text-gray-600">Show:</span>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                filter === 'all' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('requirements')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                filter === 'requirements' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Requirements
            </button>
            <button
              onClick={() => setFilter('wbs')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                filter === 'wbs' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              WBS
            </button>
          </div>
        </div>

        {/* Diff Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!hasChanges ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Changes Detected</h3>
              <p className="text-gray-500">
                These two baselines are identical.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Requirements Diff */}
              {(filter === 'all' || filter === 'requirements') && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Requirements Changes
                  </h3>
                  <DiffSection 
                    title="Added Requirements" 
                    items={requirements?.added} 
                    changeType="added" 
                  />
                  <DiffSection 
                    title="Removed Requirements" 
                    items={requirements?.removed} 
                    changeType="removed" 
                  />
                  <DiffSection 
                    title="Modified Requirements" 
                    items={requirements?.modified} 
                    changeType="modified" 
                  />
                </div>
              )}

              {/* WBS Diff */}
              {(filter === 'all' || filter === 'wbs') && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    WBS Changes
                  </h3>
                  <DiffSection 
                    title="Added WBS Nodes" 
                    items={wbs_nodes?.added} 
                    changeType="added" 
                  />
                  <DiffSection 
                    title="Removed WBS Nodes" 
                    items={wbs_nodes?.removed} 
                    changeType="removed" 
                  />
                  <DiffSection 
                    title="Modified WBS Nodes" 
                    items={wbs_nodes?.modified} 
                    changeType="modified" 
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <div className="text-xs text-gray-500">
            Comparison generated at {new Date().toLocaleString()}
          </div>
          <div className="flex items-center gap-3">
            {onCreateChangeRequest && hasChanges && (
              <button
                onClick={onCreateChangeRequest}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white transition-colors"
                data-testid="create-change-request-btn"
              >
                <Plus className="w-4 h-4" />
                Create Change Request
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </WidgetErrorBoundary>
  );
};

export default BaselineDiffViewer;
